import { toast } from '@iguanads/react'
import * as Sentry from '@sentry/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import ordersService from '@/api/orders.service'
import { deleteOrderAlert } from '@/api/orders/delete-order-alert'
import { getOrderAlerts } from '@/api/orders/get-order-alerts'
import { OrderStatusEnum } from '@/enums/order.enum'
import useAuth from '@/hooks/useAuth'
import useLiveOrders from '@/hooks/useLiveOrders'
import type { PickupOrderAlertType } from '@/types/order-alerts.types'
import {
  ensureNotificationPermission,
  showBrowserNotification,
} from '@/utils/browser-notification'
import {
  getAlertIdentity,
  isConfirmAlert,
  isReadyAlert,
  ORDER_ALERTS_POLL_MS,
  readNotifiedAlertIds,
  writeNotifiedAlertIds,
} from '@/utils/pickup-order-alerts'

function usePickupOrderReminders() {
  const { t } = useTranslation()
  const { setOrders } = useLiveOrders()
  const { merchantsSelected } = useAuth()
  const queryClient = useQueryClient()
  const [isUpdating, setIsUpdating] = useState(false)
  const notifiedAlertIdsRef = useRef<Set<string>>(
    new Set(readNotifiedAlertIds())
  )

  const merchantIds = merchantsSelected.map((merchant) => merchant.id)

  const { data: alerts = [] } = useQuery({
    queryKey: ['order-alerts', ...merchantIds],
    queryFn: () => getOrderAlerts(merchantIds),
    enabled: merchantIds.length > 0,
    refetchInterval: ORDER_ALERTS_POLL_MS,
  })

  const invalidateAlerts = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: ['order-alerts'],
    })
  }, [queryClient])

  const { mutateAsync: dismissAlert } = useMutation({
    mutationFn: deleteOrderAlert,
  })

  const confirmAlerts = useMemo(
    () => alerts.filter(isConfirmAlert),
    [alerts]
  )
  const activePopup = confirmAlerts[0] ?? null

  useEffect(() => {
    void ensureNotificationPermission()
  }, [])

  useEffect(() => {
    const activeIds = new Set(alerts.map(getAlertIdentity))
    let notifiedChanged = false

    notifiedAlertIdsRef.current.forEach((id) => {
      if (!activeIds.has(id)) {
        notifiedAlertIdsRef.current.delete(id)
        notifiedChanged = true
      }
    })

    alerts.forEach((alert) => {
      const identity = getAlertIdentity(alert)
      if (notifiedAlertIdsRef.current.has(identity)) return

      const customerName =
        alert.customerName || t('dialogs.pickupOrderReminders.customerFallback')
      const copyKey = isReadyAlert(alert) ? 'preparingPush' : 'readyPush'
      const title = t(`dialogs.pickupOrderReminders.${copyKey}.title`, {
        orderNumber: alert.shortId,
        customerName,
      })
      const message = t(`dialogs.pickupOrderReminders.${copyKey}.message`)

      showBrowserNotification(title, message)
      toast({
        type: 'info',
        title,
        message,
        actionButton: false,
        hideCloseButton: false,
      })

      notifiedAlertIdsRef.current.add(identity)
      notifiedChanged = true
    })

    if (notifiedChanged) {
      writeNotifiedAlertIds(Array.from(notifiedAlertIdsRef.current))
    }
  }, [alerts, t])

  const removeAlertLocally = useCallback(
    (orderId: string) => {
      queryClient.setQueriesData<PickupOrderAlertType[]>(
        { queryKey: ['order-alerts'] },
        (current) => current?.filter((alert) => alert.orderId !== orderId) ?? []
      )
    },
    [queryClient]
  )

  const dismissActivePopup = useCallback(async () => {
    if (!activePopup) return

    try {
      await dismissAlert(activePopup.orderId)
      removeAlertLocally(activePopup.orderId)
    } catch (error) {
      Sentry.captureException(error)
      toast({
        type: 'error',
        title: t('dialogs.pickupOrderReminders.errors.updateTitle'),
        message: t('dialogs.pickupOrderReminders.errors.updateMessage'),
      })
    }
  }, [activePopup, dismissAlert, removeAlertLocally, t])

  const markOrderReady = useCallback(async () => {
    if (!activePopup || !isReadyAlert(activePopup)) return

    setIsUpdating(true)
    try {
      await ordersService.updateOrderStatus({
        status: OrderStatusEnum.READY,
        orderId: activePopup.orderId,
      })

      setOrders((prev) =>
        prev.map((order) =>
          order.orderId === activePopup.orderId
            ? { ...order, lastStatus: OrderStatusEnum.READY }
            : order
        )
      )
      removeAlertLocally(activePopup.orderId)
      await invalidateAlerts()
    } catch (error) {
      Sentry.captureException(error)
      toast({
        type: 'error',
        title: t('dialogs.pickupOrderReminders.errors.updateTitle'),
        message: t('dialogs.pickupOrderReminders.errors.updateMessage'),
      })
    } finally {
      setIsUpdating(false)
    }
  }, [activePopup, invalidateAlerts, removeAlertLocally, setOrders, t])

  const markOrderPickedUp = useCallback(async () => {
    if (!activePopup || isReadyAlert(activePopup)) return

    setIsUpdating(true)
    try {
      await ordersService.updateOrderStatus({
        status: OrderStatusEnum.PICKED,
        orderId: activePopup.orderId,
      })

      setOrders((prev) =>
        prev.filter((order) => order.orderId !== activePopup.orderId)
      )
      removeAlertLocally(activePopup.orderId)
      await invalidateAlerts()
    } catch (error) {
      Sentry.captureException(error)
      toast({
        type: 'error',
        title: t('dialogs.pickupOrderReminders.errors.updateTitle'),
        message: t('dialogs.pickupOrderReminders.errors.updateMessage'),
      })
    } finally {
      setIsUpdating(false)
    }
  }, [activePopup, invalidateAlerts, removeAlertLocally, setOrders, t])

  return {
    activePopup,
    isUpdating,
    dismissActivePopup,
    markOrderReady,
    markOrderPickedUp,
  }
}

export { usePickupOrderReminders }
